// The editor's "To look at": flags on the article, open ones first. A cited
// register row that changed shows each column that moved, before and after,
// and the Register updates change that wrote it; a retired row shows what it
// read; a stale screenshot shows itself, the version it was taken on and the
// one the fleet is offered now. Done closes a flag the article was changed
// for; No change needed one it wasn't. docs/wip/SCOUT_HELP.md

import { useState } from 'react';
import { api } from '../../../services/api';
import { APPS, errorText, useResolveFlag, type HelpFlag } from './data/help';

const button = 'inline-flex min-h-[40px] items-center rounded-[10px] px-3 text-[15px] font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 disabled:opacity-40';

type Columns = Record<string, string>;
const columnsOf = (v: unknown): Columns => (v && typeof v === 'object' ? (v as Columns) : {});

function title(f: HelpFlag): string {
  const id = String(f.detail.docId ?? '');
  if (f.kind === 'row_changed') return `${id} changed in the register`;
  if (f.kind === 'row_retired') return `${id} is retired from the register`;
  if (f.kind === 'stale_image') return 'A screenshot may be out of date';
  return f.key;
}

function Moved({ before, after }: { before: Columns; after: Columns }) {
  const cols = [...new Set([...Object.keys(before), ...Object.keys(after)])].filter((c) => (before[c] ?? '') !== (after[c] ?? ''));
  if (!cols.length) return <p className="text-[15px] text-slate-400">The text reads the same; only its place in the register moved.</p>;
  return (
    <dl className="space-y-2">
      {cols.map((c) => (
        <div key={c} className="space-y-1">
          <dt className="text-[13px] font-semibold text-slate-400">{c}</dt>
          <dd className="space-y-1 text-[15px]">
            <p className="rounded-md bg-red-400/10 px-2 py-1 text-red-100 line-through decoration-red-300/60">{before[c] || 'Empty'}</p>
            <p className="rounded-md bg-emerald-400/10 px-2 py-1 text-emerald-100">{after[c] || 'Empty'}</p>
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** The app as Needs software names it; PolarisOS as the Scout does. */
const appName = (key: string) => (key === 'launcher' ? 'PolarisOS' : APPS.find((a) => a.key === key)?.name ?? key);

function Stale({ detail }: { detail: Record<string, unknown> }) {
  const { mediaId, alt, takenOn, offered } = detail as { mediaId: string; alt: string; takenOn: { app: string; version: string }; offered: string };
  return (
    <div className="flex gap-3">
      <img src={api.adminHelpMediaUrl(mediaId)} alt={alt} className="h-16 w-24 shrink-0 rounded-md border border-slate-400/20 bg-slate-950 object-contain" />
      <p className="text-[15px] text-slate-300">
        {alt} was taken on {appName(takenOn.app)} {takenOn.version}; the fleet is now offered {offered}. Check it still matches the Scout.
      </p>
    </div>
  );
}

function OpenFlag({ f, articleId }: { f: HelpFlag; articleId: string }) {
  const resolve = useResolveFlag(articleId);
  const [reason, setReason] = useState('');
  const name = title(f);
  const close = (status: 'resolved' | 'no_change') => resolve.mutate({ id: f.id, status, reason: reason.trim() || null });
  const { module, version, acceptedBy } = f.detail as { module?: string; version?: string; acceptedBy?: string | null };
  return (
    <li role="group" aria-label={name} className="space-y-2.5 border-t border-slate-400/10 px-4 py-3 first:border-t-0">
      <p className="text-[15px] font-semibold text-amber-200">{name}</p>
      {module && version && <p className="text-[14px] text-slate-400">{module} {version}{acceptedBy ? ` · accepted by ${acceptedBy}` : ''}</p>}
      {f.kind === 'row_changed' && <Moved before={columnsOf(f.detail.before)} after={columnsOf(f.detail.after)} />}
      {f.kind === 'row_retired' && <p className="text-[15px] text-slate-300">It read: {columnsOf(f.detail.before).Element || 'nothing'}</p>}
      {f.kind === 'stale_image' && <Stale detail={f.detail} />}
      <label className="flex flex-col gap-1 text-[13px] font-medium text-slate-400">
        Why (optional)
        <input
          className="min-h-[40px] rounded-[10px] border border-slate-400/20 bg-slate-900/60 px-3 text-[15px] text-white focus:border-cyan-400/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/40"
          value={reason}
          maxLength={500}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      {resolve.isError && <p role="alert" className="text-[15px] text-red-300">{errorText(resolve.error)}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={resolve.isPending} onClick={() => close('resolved')} className={`${button} bg-cyan-600 text-white hover:bg-cyan-500`}>Done</button>
        <button type="button" disabled={resolve.isPending} onClick={() => close('no_change')} className={`${button} border border-slate-400/25 text-white hover:bg-slate-400/10`}>No change needed</button>
      </div>
    </li>
  );
}

export function HelpFlags({ flags, articleId }: { flags: HelpFlag[]; articleId: string }) {
  if (!flags.length) return null;
  const open = flags.filter((f) => f.status === 'open');
  const closed = flags.filter((f) => f.status !== 'open');
  return (
    <section aria-label="To look at" className="space-y-2">
      <h2 className="text-[17px] font-semibold text-white">To look at</h2>
      {open.length > 0
        ? <ul className="overflow-hidden rounded-2xl border border-amber-400/25 bg-amber-400/[0.04]">{open.map((f) => <OpenFlag key={f.id} f={f} articleId={articleId} />)}</ul>
        : <p className="text-[15px] text-slate-400">Nothing open.</p>}
      {closed.length > 0 && (
        <details className="text-[15px] text-slate-400">
          <summary className="min-h-[40px] cursor-pointer py-2">Closed ({closed.length})</summary>
          <ul className="space-y-1.5">
            {closed.map((f) => (
              <li key={f.id}>
                <span className="text-slate-300">{title(f)}</span>
                {' · '}{f.status === 'no_change' ? 'No change needed' : 'Done'} · {f.resolvedByName ?? 'Someone'}{f.reason ? ` · ${f.reason}` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
